import { useArtifacts } from "../../lib/api/hooks";
import { useAppStore } from "@/lib/store";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Download, Search, FileText, X, Loader2, Check, Copy, RefreshCw, AlertCircle } from "lucide-react";
import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const MAX_PREVIEW_SIZE = 256 * 1024; // 256KB

function isPreviewable(path: string, size: number | null): boolean {
  if (size && size > MAX_PREVIEW_SIZE) {
    return false;
  }
  const ext = path.split(".").pop()?.toLowerCase();
  return ["md", "txt", "json", "jsonl", "log", "yml", "yaml"].includes(ext || "");
}

function getPreviewType(path: string): "markdown" | "json" | "text" {
  const ext = path.split(".").pop()?.toLowerCase();
  if (ext === "md") return "markdown";
  if (ext === "json" || ext === "jsonl") return "json";
  return "text";
}

export function ArtifactsPanel() {
  const { selectedSessionId } = useAppStore();
  const queryClient = useQueryClient();
  const { 
    data: artifactsData, 
    isLoading, 
    isError, 
    error,
    refetch 
  } = useArtifacts(selectedSessionId);
  const [searchQuery, setSearchQuery] = useState("");
  const [previewPath, setPreviewPath] = useState<string | null>(null);
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [copiedPath, setCopiedPath] = useState<string | null>(null);

  const artifacts = useMemo(
    () => artifactsData?.artifacts || [],
    [artifactsData?.artifacts]
  );

  const filteredArtifacts = useMemo(() => {
    if (!searchQuery.trim()) return artifacts;
    const query = searchQuery.toLowerCase();
    return artifacts.filter((a) => a.path.toLowerCase().includes(query));
  }, [artifacts, searchQuery]);

  // Group artifacts by directory
  const groupedArtifacts = useMemo(() => {
    const groups: Record<string, typeof artifacts> = {};
    const root: typeof artifacts = [];

    filteredArtifacts.forEach((artifact) => {
      const parts = artifact.path.split("/");
      if (parts.length === 1) {
        root.push(artifact);
      } else {
        const dir = parts.slice(0, -1).join("/");
        if (!groups[dir]) {
          groups[dir] = [];
        }
        groups[dir].push(artifact);
      }
    });

    return { root, groups };
  }, [filteredArtifacts]);

  const handleDownload = async (path: string) => {
    const { apiUrl, apiToken } = useAppStore.getState();
    const url = `${apiUrl}/api/v1/sessions/${selectedSessionId}/artifacts/${encodeURIComponent(path)}`;

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${apiToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download artifact");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = path.split("/").pop() || "artifact";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download artifact");
    }
  };

  const handlePreview = async (path: string) => {
    setPreviewPath(path);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewContent(null);

    const { apiUrl, apiToken } = useAppStore.getState();
    const url = `${apiUrl}/api/v1/sessions/${selectedSessionId}/artifacts/${encodeURIComponent(path)}`;

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${apiToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load artifact: ${response.statusText}`);
      }

      const blob = await response.blob();
      const text = await blob.text();

      if (text.length > MAX_PREVIEW_SIZE) {
        setPreviewError(`File too large to preview (${(text.length / 1024).toFixed(1)}KB). Download to view.`);
        return;
      }

      setPreviewContent(text);
    } catch (error) {
      console.error("Preview failed:", error);
      setPreviewError(error instanceof Error ? error.message : "Failed to load preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCopyPath = async (path: string) => {
    try {
      await navigator.clipboard.writeText(path);
      setCopiedPath(path);
      setTimeout(() => setCopiedPath(null), 2000);
    } catch (error) {
      console.error("Failed to copy path:", error);
    }
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["artifacts", selectedSessionId] });
    refetch();
  };

  if (!selectedSessionId) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <div className="text-center">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p className="text-sm">Select a session to view artifacts</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="h-8 w-8 mx-auto mb-4 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading artifacts...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="border-destructive/50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 text-destructive">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium">Failed to load artifacts</p>
              <p className="text-sm text-muted-foreground mt-1">
                {error instanceof Error ? error.message : "Unknown error occurred"}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-border/50 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold">
              Session Artifacts
              {artifacts.length > 0 && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({artifacts.length} {artifacts.length === 1 ? "file" : "files"})
                </span>
              )}
            </CardTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              title="Refresh artifacts"
              className="h-8 w-8"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Search */}
          {artifacts.length > 0 && (
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search artifacts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9 text-sm"
                />
              </div>
            </div>
          )}

          {/* Artifacts List */}
          {filteredArtifacts.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {searchQuery ? "No artifacts match your search" : "No artifacts yet"}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Root files */}
              {groupedArtifacts.root.length > 0 && (
                <div className="space-y-2">
                  {groupedArtifacts.root.map((artifact) => (
                    <ArtifactItem
                      key={artifact.path}
                      artifact={artifact}
                      onDownload={handleDownload}
                      onPreview={handlePreview}
                      onCopyPath={handleCopyPath}
                      copiedPath={copiedPath}
                    />
                  ))}
                </div>
              )}

              {/* Grouped by directory */}
              {Object.entries(groupedArtifacts.groups)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([dir, dirArtifacts]) => (
                  <div key={dir} className="space-y-2">
                    <div className="text-xs font-medium text-muted-foreground px-1">
                      {dir}/
                    </div>
                    {dirArtifacts.map((artifact) => (
                      <ArtifactItem
                        key={artifact.path}
                        artifact={artifact}
                        onDownload={handleDownload}
                        onPreview={handlePreview}
                        onCopyPath={handleCopyPath}
                        copiedPath={copiedPath}
                      />
                    ))}
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Modal */}
      {previewPath && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col">
            <CardHeader className="pb-3 flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold truncate">
                  {previewPath}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setPreviewPath(null);
                    setPreviewContent(null);
                    setPreviewError(null);
                  }}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-auto pt-0">
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : previewError ? (
                <div className="py-8 text-center text-sm text-destructive">
                  {previewError}
                </div>
              ) : previewContent ? (
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  {getPreviewType(previewPath) === "markdown" ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {previewContent}
                    </ReactMarkdown>
                  ) : getPreviewType(previewPath) === "json" ? (
                    <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-xs">
                      <code>
                        {(() => {
                          try {
                            return JSON.stringify(JSON.parse(previewContent), null, 2);
                          } catch {
                            return previewContent;
                          }
                        })()}
                      </code>
                    </pre>
                  ) : (
                    <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-xs whitespace-pre-wrap">
                      <code>{previewContent}</code>
                    </pre>
                  )}
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}

interface ArtifactItemProps {
  artifact: { path: string; size: number | null; modified: string | null };
  onDownload: (path: string) => void;
  onPreview: (path: string) => void;
  onCopyPath: (path: string) => void;
  copiedPath: string | null;
}

function ArtifactItem({
  artifact,
  onDownload,
  onPreview,
  onCopyPath,
  copiedPath,
}: ArtifactItemProps) {
  const canPreview = isPreviewable(artifact.path, artifact.size);
  const fileName = artifact.path.split("/").pop() || artifact.path;

  return (
    <div className="flex items-center justify-between p-3 rounded-lg border border-border/50 hover:bg-accent/50 transition-colors group">
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate text-foreground">
            {fileName}
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
            {artifact.size && (
              <span>{(artifact.size / 1024).toFixed(2)} KB</span>
            )}
            {artifact.modified && (
              <span>
                {new Date(artifact.modified).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {canPreview && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onPreview(artifact.path)}
            title="Preview"
            className="h-8 w-8"
          >
            <FileText className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onCopyPath(artifact.path)}
          title="Copy path"
          className="h-8 w-8"
        >
          {copiedPath === artifact.path ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDownload(artifact.path)}
          title="Download"
          className="h-8 w-8"
        >
          <Download className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
